import { TUTOR_REACH, TUTORIAL_BOUGHT, TUTORIAL_EXIT_QUEST, TUTORIAL_FIGHTS, TUTORIAL_FUSED, TUTORIAL_FUSED_FROM, TUTORIAL_RESOURCE, TUTORIAL_SET, TUTORIAL_UPGRADE_LEVEL, TUTORIAL_UPGRADE_MATERIAL, } from '../data/tutorial.js';
import { getItem } from '../data/items.js';
import { prologueQuests } from '../data/quests.js';
import { fusionCost } from './fusion.js';
import { carriedEntries } from './inventory.js';
import { buyPrice, sellValue } from './merchant.js';
import { upgradeCost } from './upgrade.js';
export const TUTORIAL_STAGES = [
    'hunt',
    'sell',
    'buy',
    'fuse',
    'wear',
    'forge',
    'leave',
];
/** The five pieces besides the sword, bought or given, then worn. */
const ARMOUR = TUTORIAL_SET.filter((id) => id !== TUTORIAL_FUSED_FROM);
/** What the pieces the player buys cost; the swords are the smelter's (ADR 0078). */
export function tutorialSetCost() {
    return TUTORIAL_BOUGHT.reduce((sum, id) => sum + buyPrice(requireItem(id)), 0);
}
/** What the fusion costs at the smelter. */
function fuseGold() {
    return fusionCost(requireItem(TUTORIAL_FUSED_FROM)) ?? 0;
}
/** What the smith asks for the rare sword's first level. */
function forgeGold() {
    return upgradeCost(requireItem(TUTORIAL_FUSED), TUTORIAL_UPGRADE_LEVEL - 1)?.gold ?? 0;
}
/** Everything the introduction makes the player pay: the set, the fusion, the smith. */
export function tutorialCost() {
    return tutorialSetCost() + fuseGold() + forgeGold();
}
/**
 * The most a rat leaves in the camp: enough pelts that `TUTORIAL_FIGHTS`
 * fights pay for the whole path. Its killer gets only what is still due
 * (`tutorialPeltsDue`), and the flint comes apart (`tutorialNeedsFlint`).
 */
export function tutorialLoot() {
    return [
        {
            templateId: TUTORIAL_RESOURCE,
            quantity: Math.ceil(tutorialCost() / TUTORIAL_FIGHTS / requireItem(TUTORIAL_RESOURCE).sellPrice),
        },
    ];
}
/**
 * The pelts a rat leaves its killer: what the rest of the path still asks,
 * once the gold in hand, the pelts in the bag and those waiting on the
 * ground (`onGround`) are counted, and never more than `tutorialLoot`'s. Once
 * everything is paid for, nothing: the camp is no place to farm gold.
 */
export function tutorialPeltsDue(inventory, onGround = 0) {
    const [pelts] = tutorialLoot();
    const needed = Math.ceil(tutorialShortfall(inventory) / requireItem(TUTORIAL_RESOURCE).sellPrice);
    return Math.max(0, Math.min(pelts?.quantity ?? 0, needed - onGround));
}
/** The flint a rat leaves to the one who needs it, for the smith's lesson. */
export const TUTORIAL_FLINT_DROP = { templateId: TUTORIAL_UPGRADE_MATERIAL, quantity: 1 };
/**
 * Whether a rat leaves this character a flint: only while the smelter's
 * lesson is under way (`lessonUnderWay`), whose giver hands one over, for
 * whoever lost it; none in the bag, and nothing ever taken to the smith, the
 * rare sword included. One flint makes one level; staying in the camp to
 * farm them makes nothing more.
 */
export function tutorialNeedsFlint(inventory, lessonUnderWay) {
    return (lessonUnderWay &&
        tutorialFlint(inventory) === 0 &&
        held(inventory).every((item) => item.upgradeLevel === 0));
}
/**
 * What the camp's merchant will not buy back (ADR 0078): the lessons' pieces,
 * the rare sword and the smith's flint. Sold on the way, they left a
 * newcomer short of the path with no pelts to pay for it again.
 */
export function campKeeps(templateId) {
    return (TUTORIAL_SET.includes(templateId) ||
        templateId === TUTORIAL_FUSED ||
        templateId === TUTORIAL_UPGRADE_MATERIAL);
}
/**
 * A kill's experience in the camp (ADR 0078): none once the first lesson is
 * turned in. The rats are there to learn on, not to stay among.
 */
export function campKillXp(xp, huntDone) {
    return huntDone ? 0 : xp;
}
function worn(inventory) {
    return new Set(Object.values(inventory.equipped).map((item) => item.templateId));
}
function held(inventory) {
    return [
        ...carriedEntries(inventory).map(({ item }) => item),
        ...Object.values(inventory.equipped),
    ];
}
function count(inventory, templateId) {
    return carriedEntries(inventory).reduce((sum, { item }) => sum + (item.templateId === templateId ? item.quantity : 0), 0);
}
/**
 * The set's pieces the character carries or wears, by their base id: the
 * rare sword stands for the wooden one it was melted from.
 */
export function tutorialOwned(inventory) {
    const ids = new Set(held(inventory).map((item) => item.templateId));
    if (ids.has(TUTORIAL_FUSED))
        ids.add(TUTORIAL_FUSED_FROM);
    return new Set(TUTORIAL_SET.filter((id) => ids.has(id)));
}
/** How many of the set's pieces are worn; the rare sword counts for the sword. */
export function tutorialEquipped(inventory) {
    const on = worn(inventory);
    if (on.has(TUTORIAL_FUSED))
        on.add(TUTORIAL_FUSED_FROM);
    return TUTORIAL_SET.filter((id) => on.has(id)).length;
}
/** The rare sword's best level, or undefined before the fusion. */
export function tutorialFusedLevel(inventory) {
    const levels = held(inventory)
        .filter((item) => item.templateId === TUTORIAL_FUSED)
        .map((item) => item.upgradeLevel);
    return levels.length === 0 ? undefined : Math.max(...levels);
}
export function tutorialPelts(inventory) {
    return count(inventory, TUTORIAL_RESOURCE);
}
export function tutorialFlint(inventory) {
    return count(inventory, TUTORIAL_UPGRADE_MATERIAL);
}
/** Gold the rest of the path still asks: the pieces not bought, the fusion, the smith. */
export function tutorialRemainingCost(inventory) {
    const owned = tutorialOwned(inventory);
    const level = tutorialFusedLevel(inventory);
    const pieces = TUTORIAL_BOUGHT.filter((id) => !owned.has(id)).reduce((sum, id) => sum + buyPrice(requireItem(id)), 0);
    const fusion = level === undefined ? fuseGold() : 0;
    const forge = (level ?? 0) >= TUTORIAL_UPGRADE_LEVEL ? 0 : forgeGold();
    return pieces + fusion + forge;
}
/**
 * Gold still missing once the pelts in the bag are sold: above zero, the
 * guide sends the player back to the rats.
 */
export function tutorialShortfall(inventory) {
    const pelts = sellValue(requireItem(TUTORIAL_RESOURCE), tutorialPelts(inventory));
    return Math.max(0, tutorialRemainingCost(inventory) - inventory.gold - pelts);
}
export function tutorialStage(inventory) {
    if (tutorialComplete(inventory))
        return 'leave';
    const on = worn(inventory);
    if (tutorialFusedLevel(inventory) !== undefined) {
        const dressed = on.has(TUTORIAL_FUSED) && ARMOUR.every((id) => on.has(id));
        return dressed ? 'forge' : 'wear';
    }
    const owned = tutorialOwned(inventory);
    if (owned.size === TUTORIAL_SET.length)
        return 'fuse';
    // The merchant's gifts come before any sale: only a piece bought means the shop was reached.
    const bought = TUTORIAL_BOUGHT.some((id) => owned.has(id));
    if (inventory.gold > 0 || bought)
        return 'buy';
    return tutorialPelts(inventory) > 0 ? 'sell' : 'hunt';
}
/**
 * The way out opens once the five pieces are worn with the rare sword, taken
 * to +1: every lesson of the camp learnt, carried or not.
 */
export function tutorialComplete(inventory) {
    const on = worn(inventory);
    const sword = inventory.equipped.weapon;
    return (ARMOUR.every((id) => on.has(id)) &&
        sword?.templateId === TUTORIAL_FUSED &&
        sword.upgradeLevel >= TUTORIAL_UPGRADE_LEVEL);
}
/**
 * Whether the camp's arch lets the character through (ADR 0066): once the
 * smith's lesson is turned in. A character that walked the old path to its
 * end before the quests, and took none of them, keeps its way out.
 */
export function tutorialExitOpen(inventory, quests) {
    if (quests.done.has(TUTORIAL_EXIT_QUEST))
        return true;
    const taken = prologueQuests().some((quest) => quests.done.has(quest.id) || quests.active.has(quest.id));
    return !taken && tutorialComplete(inventory);
}
/**
 * What the tutor hands a character whose account already finished the camp
 * (ADR 0066) and who would rather go straight to Havrebourg: what the path
 * would have left on them, the rare sword at +1.
 */
export function tutorialSkipKit() {
    return [
        ...ARMOUR.map((templateId) => ({ templateId, upgradeLevel: 0 })),
        { templateId: TUTORIAL_FUSED, upgradeLevel: TUTORIAL_UPGRADE_LEVEL },
    ];
}
/** Within `TUTOR_REACH` tiles of the tutor, diagonals included. */
export function withinTutorReach(at, tutors) {
    return tutors.some((tutor) => Math.max(Math.abs(tutor.x - at.x), Math.abs(tutor.y - at.y)) <= TUTOR_REACH);
}
function requireItem(id) {
    const item = getItem(id);
    if (item === undefined)
        throw new Error(`Unknown tutorial item: ${id}`);
    return item;
}
//# sourceMappingURL=tutorial.js.map